import DoppelModal from '@/components/doppel_design/doppel_modal'
import { AlertMovementReasons, ReportStatus } from '@/generated/enums'
import { ReactNode, useState } from 'react'
import {
  QUEUE_MOVEMENTS_TO_VALID_REASONS,
  createQueueMovementKey,
  getDescriptionForReason,
} from './queue_movements_constants'
import {
  Divider,
  FormControl,
  Input,
  Radio,
  RadioGroup,
  Spacer,
  Stack,
  Text,
} from '@chakra-ui/react'
import DoppelFormLabel from '@/components/shared/forms/doppel_form_label'
import { DOPPEL_BREACH_RED } from '@/utils/style'
import { getStatusLabel, ReportType } from '@/utils/constants'

type LateralMovementsSurveyModalProps = {
  isOpen: boolean
  onClose: () => void
  onSubmission: (reason: AlertMovementReasons, additionalNotes: string | null) => void
  prevQueue: ReportStatus
  nextQueue: ReportStatus
}

const LateralMovementsSurveyModal = ({
  isOpen,
  onClose,
  onSubmission,
  prevQueue,
  nextQueue,
}: LateralMovementsSurveyModalProps) => {
  const queueMovementKey = createQueueMovementKey(prevQueue, nextQueue)
  const possibleReasons = QUEUE_MOVEMENTS_TO_VALID_REASONS.get(queueMovementKey)

  const [selectedReason, setSelectedReason] = useState(null)
  const [additionalNotes, setAdditionalNotes] = useState('')

  function reset() {
    setSelectedReason(null)
    setAdditionalNotes('')
  }

  function renderMovementReasons() {
    return (
      <FormControl isRequired>
        <DoppelFormLabel
          formLabel={`Why are you moving this alert from ${getStatusLabel(
            prevQueue,
            ReportType.DOMAINS,
          )} to ${getStatusLabel(nextQueue, ReportType.DOMAINS)}?`}
          helperText="Select the reason for the movement."
        />

        <RadioGroup
          onChange={(val) => setSelectedReason(val as AlertMovementReasons)}
          value={selectedReason}
        >
          <Stack>
            {possibleReasons?.map((reason: AlertMovementReasons) => (
              <Radio key={reason} size="sm" value={reason}>
                {getDescriptionForReason(reason)}
              </Radio>
            ))}

            <Radio
              key={AlertMovementReasons.OTHER}
              size="sm"
              value={AlertMovementReasons.OTHER}
            >
              <Text fontWeight="bold">
                {getDescriptionForReason(AlertMovementReasons.OTHER)}
              </Text>
            </Radio>
          </Stack>
        </RadioGroup>
      </FormControl>
    )
  }

  function renderAdditionalNotes() {
    return (
      <FormControl isRequired={selectedReason === AlertMovementReasons.OTHER}>
        <DoppelFormLabel
          formLabel="Additional notes"
          helperText="Enter any additional notes, reasoning, or context."
        />

        <Input
          onChange={(e) => setAdditionalNotes(e.target.value)}
          placeholder="Enter additional notes"
          value={additionalNotes}
        />
      </FormControl>
    )
  }

  function renderFormWarning(): ReactNode {
    let warningText = ''
    if (selectedReason === AlertMovementReasons.OTHER && !additionalNotes) {
      warningText = 'Please add some additional notes, since you selected other.'
    } else if (!selectedReason) {
      warningText = 'Please select a reason for this movement.'
    }

    return warningText ? (
      <Text color={DOPPEL_BREACH_RED} fontSize={'small'}>
        {warningText}
      </Text>
    ) : (
      <></>
    )
  }

  function renderBody() {
    return (
      <Stack spacing={2}>
        {renderMovementReasons()}

        <Spacer />

        <Divider />

        <Spacer />

        {renderAdditionalNotes()}

        {renderFormWarning()}
      </Stack>
    )
  }

  return (
    <DoppelModal
      body={renderBody()}
      isDisabled={
        !(
          selectedReason &&
          (selectedReason === AlertMovementReasons.OTHER ? !!additionalNotes : true)
        )
      }
      isOpen={isOpen}
      onClose={() => {
        onClose()
        reset()
      }}
      primaryAction={() => {
        onSubmission(selectedReason, additionalNotes)
        reset()
      }}
      primaryLabel="Update Asset"
      size="lg"
      title={'Alert Movement Annotations'}
    ></DoppelModal>
  )
}

export default LateralMovementsSurveyModal
